/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { times } from 'lodash';

import FadeIn from 'legacy/common/components/transition/FadeIn';
import DateTime, {
  InputStyles
} from 'legacy/common/components/wysiwyg-form/DateTime';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import {
  RECURRING_FREQUENCY_LABELS,
  RecurringFrequency
} from 'legacy/events/types';
import SelectDropdown from 'components/SelectDropdown';

import ConfirmModal from './components/ConfirmModal';
import { computeNextMonthNthDay } from './helpers/computeNextMonthNthDay';

interface Props {
  eventId: string;
  seriesId: string;
  initialSeriesStartDate: string;
  initialSeriesEndDate: string;
  initialEventStartDate: string;
  isLoading: boolean;
  showTierDatesWarning: boolean;
  createDraft: () => void;
  canEdit: boolean;
}

const RecurringFrequencyData = {
  [RecurringFrequency.Weekly]: { value: 1, type: 'week' },
  [RecurringFrequency.BiWeekly]: { value: 2, type: 'weeks' },
  [RecurringFrequency.Monthly]: { value: 1, type: 'month' }
};

const recurringFrequencyOptions = [
  {
    value: RecurringFrequency.Weekly,
    label: RECURRING_FREQUENCY_LABELS[RecurringFrequency.Weekly]
  },
  {
    value: RecurringFrequency.BiWeekly,
    label: RECURRING_FREQUENCY_LABELS[RecurringFrequency.BiWeekly]
  },
  {
    value: RecurringFrequency.Monthly,
    label: RECURRING_FREQUENCY_LABELS[RecurringFrequency.Monthly]
  }
];

const RecurringSettingsForm = ({
  eventId,
  createDraft,
  isLoading,
  seriesId,
  initialEventStartDate,
  initialSeriesStartDate,
  initialSeriesEndDate,
  showTierDatesWarning,
  canEdit = true
}: Props) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  let isSubmitDisabled: boolean;

  if (!canEdit) {
    isSubmitDisabled = true;
  } else if (seriesId) {
    isSubmitDisabled = values.repeats && !values.endsAfter;
  } else {
    isSubmitDisabled = !values.repeats || (values.repeats && !values.endsAfter);
  }

  const toggleConfirmModal = () => {
    setIsConfirmOpen((prevState) => !prevState);
  };

  const resetEndsAfter = () => {
    if (values.endsAfter) {
      setFieldValue('endsAfter', '');
    }
  };

  /**
   * A memoized array of options for repeat frequency.
   * The options are dynamically generated based on the provided start date,
   * If no start date is provided, a single default option is returned.
   */
  const repeatsOptions = useMemo(() => {
    const defaultOption = { value: '', label: 'Does not repeat' };

    if (!values.startDate) {
      return [defaultOption];
    }

    const selectedDay = dayjs(values.startDate).format('dddd');
    return [
      defaultOption,
      ...recurringFrequencyOptions.map((option) => ({
        value: option.value,
        label: `${option.label} on ${selectedDay}`
      }))
    ];
  }, [values.startDate]);

  /**
   * A memoized array of options representing the potential end dates based on the recurrence frequency selected.
   * It generates a list of possible end dates calculated using the recurrence frequency.
   * The maximum number of option is 9 for now as we can only have a total of 10 events in a series.
   */
  const endsAfterOptions = useMemo(() => {
    const defaultOption = { value: '', label: 'Select a number of events' };

    if (!values.startDate || !values.repeats) {
      return [defaultOption];
    }

    const options = [];
    times(9).forEach((i) => {
      let actualNextEventDate: Dayjs;
      const previousDate = i > 0 ? options[i - 1].value : values.startDate;

      if (values.repeats === RecurringFrequency.Monthly) {
        actualNextEventDate = computeNextMonthNthDay(
          values.startDate,
          previousDate
        );
      } else {
        actualNextEventDate = dayjs(previousDate).add(
          RecurringFrequencyData[values.repeats].value,
          RecurringFrequencyData[values.repeats].type
        );
      }

      options.push({
        value: actualNextEventDate.toISOString(),
        // i + 2 because we need to account for the initial event and the fact that the `times` fn is 0 based.
        label: `${actualNextEventDate.format('MMMM D, YYYY')} (${i + 2} events)`
      });
    });

    return [defaultOption, ...options];
  }, [values.startDate, values.repeats]);

  const onRepeatsChange = (value) => {
    setFieldValue('repeats', value);
    resetEndsAfter();
  };

  const onEndsAfterChange = (value) => setFieldValue('endsAfter', value);

  return (
    <>
      <FadeIn>
        <div
          className="pt-[84px] md:pt-[180px] -mt-[90px] pb-48 md:w-[630px] md:mx-auto px-4"
          onClick={async () => {
            // Create a draft if event has not been created yet
            if (!eventId) {
              await createDraft();
            }
          }}
        >
          <p className="text-base font-normal text-neutral-600">
            Repeating an event creates up to 10 linked drafts with all details
            and settings copied. Each draft in the series can be edited
            individually.
          </p>
          {showTierDatesWarning && (
            <div className="bg-yellow-100 p-4 text-yellow-800 text-base font-normal mt-4 rounded-xl">
              <h3 className="text-yellow-800 text-sm font-semibold pb-2">
                REMINDER ABOUT TICKET TIERS
              </h3>
              <p className="text-base">
                If you&apos;ve set specific dates for ticket tiers, these dates
                will <em>NOT</em> automatically adjust when you modify event
                dates. Please remember to:
              </p>
              <ul className="list-disc list-inside">
                <li>Review your ticket tier start / end dates.</li>
                <li>
                  Manually update them to align with your new event schedule.
                </li>
              </ul>
            </div>
          )}
          <h3 className="text-base font-semibold pb-4 pt-9">
            Start Date & Time (Linked to First Draft)
          </h3>
          <DateTime
            name="startDate"
            dateLabel="Start date*"
            timeLabel="Start time*"
            datePlaceholder="Select a date"
            timePlaceholder="Select a time"
            min={new Date()}
            inputStyle={InputStyles.Input}
            onChange={resetEndsAfter}
            disabled={!canEdit}
          />
          <div className="flex flex-col pt-9">
            <h3 className="text-base font-semibold pb-4">Recurring Schedule</h3>
            <label className="text-base font-normal pb-1">Repeats</label>
            <SelectDropdown
              onChange={onRepeatsChange}
              value={values.repeats}
              data={repeatsOptions}
              disabled={!canEdit || !values.startDate}
            />
          </div>
          {values.repeats && (
            <div className="flex flex-col pt-7">
              <label className="text-base font-normal pb-1">
                Ends after (Max 10 events)
              </label>
              <SelectDropdown
                onChange={onEndsAfterChange}
                value={values.endsAfter}
                data={endsAfterOptions}
                disabled={!canEdit}
              />
            </div>
          )}
        </div>
      </FadeIn>
      <div className="w-full fixed left-0 bottom-0 bg-white pb-8 z-1 md:pb-4 border-t-1 border-neutral-200">
        <div className="mx-auto flex items-center justify-between pt-4 px-4 md:max-w-[500px] flex-col gap-4">
          {!canEdit && (
            <p className="text-sm text-neutral-600 font-normal">
              Recurring event settings lock once an event is published. You can
              still adjust individual unpublished events.
            </p>
          )}
          <Button
            newRadius
            type={ButtonType.Primary}
            onClick={toggleConfirmModal}
            loading={isLoading}
            disabled={isSubmitDisabled}
          >
            {seriesId ? 'Save Changes' : 'Create Events'}
          </Button>
        </div>
      </div>
      <ConfirmModal
        seriesId={seriesId}
        isOpen={isConfirmOpen}
        toggleConfirmModal={toggleConfirmModal}
        initialSeriesStartDate={initialSeriesStartDate}
        initialSeriesEndDate={initialSeriesEndDate}
        initialEventStartDate={initialEventStartDate}
      />
    </>
  );
};

export default RecurringSettingsForm;
